import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import { ArrowBack, Logout } from '@mui/icons-material';
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Amplify } from 'aws-amplify';
import { signInWithRedirect, signUp } from 'aws-amplify/auth';
import { TooltipIconButton } from 'components/common/icon/tooltip-icon-button';
import { FC, useEffect } from 'react';
import '@aws-amplify/ui-react/styles.css';
import './amplify-overrides.css';
import { deleteLocalStorageAuth } from 'utils/local-storage';
import amplifyConfigs from '../../../configs/amplify-config';
import AktifyLogoImage from '../../../public/aktify-logo.svg';

const config = amplifyConfigs();
if (config.Auth?.Cognito.userPoolId && config.Auth?.Cognito.userPoolClientId) {
  Amplify.configure(config);
}

const AktifyLogo = styled(Box)`
  margin-bottom: 20px;
  margin-right: auto;
  margin-left: calc(50% - 87px);
  height: 32px;
  content: url(${AktifyLogoImage});
`;

export interface CustomState {
  originalUrl: string;
}

export const LoginOrCreateAccount: FC<{
  originalUrl: string;
  identityProviders: string[];
  signUpEnabled: boolean;
  defaultEmail?: string;
  backToEmailPrompt: () => void;
  onGoHome: () => void;
  // Extra note shown on the "already signed in" screen (e.g. the one-account-per-pool rule).
  signedInHint?: string;
}> = ({
  originalUrl,
  identityProviders,
  signUpEnabled,
  defaultEmail,
  backToEmailPrompt,
  onGoHome,
  signedInHint,
}) => {
  const state: CustomState = {
    originalUrl,
  };
  const customState = JSON.stringify(state);

  const handleLogout = (signOut: () => void) => {
    try {
      deleteLocalStorageAuth();
      signOut();
    } catch (e) {
      console.log('error logging out', e);
    }
  };

  const loginFederatedAuth = async (customProvider: string) => {
    await signInWithRedirect({
      provider: { custom: customProvider },
      customState,
    });
  };

  // Auto-focus the appropriate field based on whether email is pre-filled
  useEffect(() => {
    const timer = setTimeout(() => {
      const fieldName = defaultEmail ? 'password' : 'username';
      const input = document.querySelector<HTMLInputElement>(`input[name="${fieldName}"]`);
      input?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [defaultEmail]);

  return (
    <Authenticator
      initialState="signIn"
      loginMechanisms={['email']}
      hideSignUp={!signUpEnabled}
      formFields={{
        signIn: {
          username: {
            defaultValue: defaultEmail,
          },
        },
        signUp: {
          email: {
            defaultValue: defaultEmail,
          },
        },
      }}
      services={{
        handleSignUp: (formData) => {
          const { username, password, options } = formData;
          const email = username.toLowerCase();

          return signUp({
            username: email,
            password,
            options: {
              ...options,
              userAttributes: {
                ...options?.userAttributes,
                email,
              },
              autoSignIn: true,
            },
          });
        },
      }}
      components={{
        Header: () => {
          const { route, toSignIn } = useAuthenticator();
          return (
            <Box>
              <IconButton
                onClick={() =>
                  route === 'signIn' || route === 'signUp' ? backToEmailPrompt() : toSignIn()
                }
                sx={{ float: 'left' }}
              >
                <ArrowBack />
              </IconButton>
              <AktifyLogo />
            </Box>
          );
        },
        SignIn: {
          Header: () => {
            const nonCognitoProviders = identityProviders.filter((ip) => ip !== 'COGNITO');
            if (nonCognitoProviders.length === 0) {
              return null;
            }
            return (
              <Stack spacing={4} marginTop={2}>
                {nonCognitoProviders.map((identityProvider) => (
                  <Button
                    variant="contained"
                    key={identityProvider}
                    onClick={() => loginFederatedAuth(identityProvider)}
                  >
                    Sign in with {identityProvider}
                  </Button>
                ))}
                <Divider />
              </Stack>
            );
          },
        },
        SignUp: {
          FormFields: () => (
            <>
              <Authenticator.SignUp.FormFields />
              <div>
                <label>Name</label>
              </div>
              <input
                type="text"
                name="name"
                className="amplify-input"
                placeholder="Enter your name"
              />
            </>
          ),
        },
      }}
    >
      {({ signOut, user }) => (
        <Stack direction="column" spacing={2} alignItems="center">
          <Typography>
            {user?.username
              ? `You are already signed in as ${user.signInDetails?.loginId ?? user.username}`
              : 'You are already signed in'}
          </Typography>
          {signedInHint && (
            <Typography color="text.secondary" textAlign="center">
              {signedInHint}
            </Typography>
          )}
          <Button onClick={onGoHome} variant="contained">
            Go Home
          </Button>{' '}
          {signOut && (
            <TooltipIconButton tooltip="Logout" onClick={() => handleLogout(signOut)}>
              <Logout />
            </TooltipIconButton>
          )}
        </Stack>
      )}
    </Authenticator>
  );
};
